import React from 'react';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import { ILoginPageConfig, parseLoginPageConfig } from '../Helpers/loginPageConfig';

// The chrome every storefront auth page sits in: sign-in, sign-up, the token landing page and the
// poll confirm page. One component so the four cannot drift apart, and so the merchant's
// loginPageConfig is applied in exactly one place rather than copied into each page.
//
// Absent config renders the historical markup unchanged — the same .login-container / .login-box the
// pages have always used, with the storefront's background and text colours applied inline as the
// sign-in page already did.

interface ILoginPageShellProps {
    /** Raw settings.loginPageConfig. Absent/malformed falls back to the default look. */
    config?: string;
    /** The storefront's name, used as the heading unless headingText overrides it. */
    storefrontName?: string;
    /** Storefront background/text colours, the pre-existing theming these pages honored. */
    backgroundColor?: string;
    textColor?: string;
    /**
     * The storefront's favicon. Applied here rather than only in Layout because these pages run
     * before any session exists, and a branded sign-in tab showing the default icon is the first
     * thing a shopper sees.
     */
    favicon?: string;
    /**
     * Class names for the backdrop and card. Default to the login page's, which the token and confirm
     * pages already used. Sign-up keeps its own `signup-*` pair so any custom CSS a merchant wrote
     * against those still lands — the shared LOOK comes from the config, not from renaming classes.
     */
    containerClassName?: string;
    boxClassName?: string;
    /**
     * Render the configured intro line. Off by default: the intro is COPY, and copy that reads "sign in
     * with your work email" is wrong on the sign-up page and meaningless on the token/confirm landings.
     * The shared thing across these four pages is the look, not the words.
     */
    showIntro?: boolean;
    children?: React.ReactNode;
}

/**
 * Colour and family only, and only when the merchant actually set them. Fluent's Input, Field and
 * Checkbox keep `style` on their ROOT and paint their INNER elements from their own theme tokens, so
 * a configured field colour never reaches the text the shopper types or the label above it. These
 * are the overrides to forward into those slots; undefined for an unset part, so a storefront with
 * no loginPageConfig keeps Fluent's own colours exactly as before.
 */
const slotTextFrom = (s: React.CSSProperties): React.CSSProperties | undefined =>
    (s.color || s.fontFamily || s.fontSize)
        // fontSize is forwarded as `inherit`, never as the value itself. In this config a fontSize is
        // a PERCENT, and elementStyleFrom has already applied it to the control's root — copying the
        // same percent onto the inner element would resolve it against that root and compound it
        // (120% of 120%). Inheriting makes the typed text, and its placeholder, take the size the
        // root already carries: applied once, where it is actually visible.
        ? { color: s.color, fontFamily: s.fontFamily, fontSize: s.fontSize ? 'inherit' : undefined }
        : undefined;

/**
 * The parsed config plus the resolved element styles, for the pieces a page renders itself (its
 * inputs and buttons live inside the page's own form, not in the shell).
 */
export function useLoginPageStyles(config?: string) {
    return React.useMemo(() => {
        const parsed: ILoginPageConfig = parseLoginPageConfig(config);
        const box = elementStyleFrom(parsed.box);
        const input = elementStyleFrom(parsed.input);
        return {
            config: parsed,
            input,
            primaryButton: elementStyleFrom(parsed.primaryButton),
            secondaryButton: elementStyleFrom(parsed.secondaryButton),
            message: elementStyleFrom(parsed.message),
            /** For the `input` slot of a field, so typed text takes the configured colour. */
            inputText: slotTextFrom(input),
            /**
             * For labels Fluent colours itself (field labels, the terms checkbox). They are card
             * text, so they follow the card rather than needing a part of their own.
             */
            cardText: slotTextFrom(box),
        };
    }, [config]);
}

const LoginPageShell: React.FC<ILoginPageShellProps> = (props) => {
    const { config } = useLoginPageStyles(props.config);

    // Same swap Layout does once a session exists, so the tab is branded from the sign-in onward.
    // No cleanup: whatever renders next (Layout, or another auth page) sets it again, and putting
    // the CRA default back mid-flow would make the tab flicker between the two.
    const favicon = props.favicon;
    React.useEffect(() => {
        if (!favicon) return;
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = favicon;
    }, [favicon]);

    // The backdrop image sits on the container as a layer UNDER any configured background, so a
    // translucent colour tints the photo rather than replacing it. backgroundColor stays applied
    // separately because the storefront's own theme colour is the historical default here.
    const backdrop: React.CSSProperties = config.backdropImageUrl
        ? {
            backgroundImage: `url(${config.backdropImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        }
        : {};

    const heading = config.headingText !== undefined ? config.headingText : props.storefrontName;

    return (
        <div
            className={props.containerClassName ?? 'login-container'}
            style={{
                backgroundColor: props.backgroundColor,
                color: props.textColor,
                minHeight: '100vh',
                ...backdrop,
                ...elementStyleFrom(config.page),
            }}
        >
            <div className={props.boxClassName ?? 'login-box'} style={elementStyleFrom(config.box)}>
                {!!config.logoUrl && (
                    <img
                        src={config.logoUrl}
                        alt={props.storefrontName ?? ''}
                        // Constrained by default so a full-size logo upload can't blow the card open;
                        // the merchant overrides height/maxWidth through the `logo` part.
                        style={{ display: 'block', margin: '0 auto 1em', maxWidth: '100%', maxHeight: 80, ...elementStyleFrom(config.logo) }}
                    />
                )}
                {/* An empty headingText is a deliberate "no heading" (a wordmark logo already says it). */}
                {!!heading && <h2 style={elementStyleFrom(config.heading)}>{heading}</h2>}
                {props.showIntro && !!config.introText && (
                    <p style={{ marginTop: 0, fontSize: 13, ...elementStyleFrom(config.intro) }}>{config.introText}</p>
                )}
                {props.children}
            </div>
        </div>
    );
};

export default LoginPageShell;
